import React, { useEffect, useMemo, useRef, useState } from 'react';
import { apiService } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import { thraceNationsForUser } from '../../utils/thraceCountry';

type Inspector = { id: number; name: string; country?: string };

/** Same order and labels as ThraceActivities*.xlsx (Data sheet). */
const ACTIVITY_COLUMNS: {
  key: string;
  label: string;
  kind: 'text' | 'number' | 'inspector';
  width?: string;
}[] = [
  { key: 'farmID', label: 'Farm ID', kind: 'text', width: 'w-20' },
  { key: 'inspectorID', label: 'InspectorID', kind: 'inspector', width: 'min-w-[8rem]' },
  { key: 'epiunitcountrycode', label: 'Village/Epiunit code', kind: 'text', width: 'w-28' },
  { key: 'enteredBy', label: 'Entered by', kind: 'text', width: 'w-24' },
  { key: 'year', label: 'Year', kind: 'number', width: 'w-16' },
  { key: 'month', label: 'Month', kind: 'number', width: 'w-14' },
  { key: 'day', label: 'Day', kind: 'number', width: 'w-14' },
  { key: 'cattle', label: 'Cattle', kind: 'number' },
  { key: 'sheep', label: 'Sheep', kind: 'number' },
  { key: 'goat', label: 'Goats', kind: 'number' },
  { key: 'pig', label: 'Pigs', kind: 'number' },
  { key: 'buffalo', label: 'W Buffalo', kind: 'number' },
  { key: 'cattleexam', label: 'Cattle Exam', kind: 'number' },
  { key: 'cattlecliposFMD', label: 'Cattle Clin + FMD', kind: 'number' },
  { key: 'cattlecliposLSD', label: 'Cattle Clin + LSD', kind: 'number' },
  { key: 'cattletested', label: 'Cattle Tested', kind: 'number' },
  { key: 'cattleseroposFMD', label: 'Cattle Sero + FMD', kind: 'number' },
  { key: 'cattleseroposLSD', label: 'Cattle Sero + LSD', kind: 'number' },
  { key: 'sheepexam', label: 'Sheep Exam', kind: 'number' },
  { key: 'sheepposFMD', label: 'Sheep Clin + FMD', kind: 'number' },
  { key: 'sheepposSGP', label: 'Sheep Clin + SPGP', kind: 'number' },
  { key: 'sheepposPPR', label: 'Sheep Clin + PPR', kind: 'number' },
  { key: 'sheeptested', label: 'Sheep Tested', kind: 'number' },
  { key: 'sheepseroposFMD', label: 'Sheep Sero + FMD', kind: 'number' },
  { key: 'sheepseroposSGP', label: 'Sheep Sero + SPGP', kind: 'number' },
  { key: 'sheepseroposPPR', label: 'Sheep Sero + PPR', kind: 'number' },
  { key: 'goatsexam', label: 'Goat Exam', kind: 'number' },
  { key: 'goatsposFMD', label: 'Goat Clin + FMD', kind: 'number' },
  { key: 'goatsposSGP', label: 'Goat Clin + SPGP', kind: 'number' },
  { key: 'goatsposPPR', label: 'Goat Clin + PPR', kind: 'number' },
  { key: 'goattested', label: 'Goat Tested', kind: 'number' },
  { key: 'goatsseroposFMD', label: 'Goat Sero + FMD', kind: 'number' },
  { key: 'goatsseroposSGP', label: 'Goat Sero + SPGP', kind: 'number' },
  { key: 'goatsseroposPPR', label: 'Goat Sero + PPR', kind: 'number' },
  { key: 'pigtested', label: 'Pig Tested', kind: 'number' },
  { key: 'pigsserosposFMD', label: 'Pig Sero + FMD', kind: 'number' },
  { key: 'buffaloesexam', label: 'Buffalo Exam', kind: 'number' },
  { key: 'buffaloesposFMD', label: 'Buffalo Clin + FMD', kind: 'number' },
  { key: 'buffaloesposLSD', label: 'Buffalo Clin + LSD', kind: 'number' },
  { key: 'buffalotested', label: 'Buffalo Tested', kind: 'number' },
  { key: 'buffaloesseroposFMD', label: 'Buffalo Sero + FMD', kind: 'number' },
  { key: 'buffaloesseroposLSD', label: 'Buffalo Sero + LSD', kind: 'number' },
  { key: 'wildtested', label: 'Wild Tested', kind: 'number' },
  { key: 'wildserosposFMD', label: 'Wild Sero + FMD', kind: 'number' },
];

type ActRow = Record<string, string>;

const emptyRow = (enteredBy = ''): ActRow => {
  const row: ActRow = {};
  const now = new Date();
  ACTIVITY_COLUMNS.forEach((c) => {
    if (c.key === 'year') row[c.key] = String(now.getFullYear());
    else if (c.key === 'month') row[c.key] = String(now.getMonth() + 1);
    else if (c.key === 'day') row[c.key] = String(now.getDate());
    else if (c.key === 'enteredBy') row[c.key] = enteredBy;
    else if (c.kind === 'number') row[c.key] = '0';
    else row[c.key] = '';
  });
  return row;
};

const ThraceActivitiesPanel: React.FC = () => {
  const { user } = useAuthStore();
  const enteredByDefault = user?.name || user?.email || '';
  const templates = useMemo(
    () =>
      thraceNationsForUser(user?.role, user?.country).map((n) => ({
        id: n.templateId,
        name: n.label,
        fileName: n.fileName,
      })),
    [user?.role, user?.country],
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [inspectors, setInspectors] = useState<Inspector[]>([]);
  const [rows, setRows] = useState<ActRow[]>(() => [emptyRow(enteredByDefault)]);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState('');
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    apiService.thrace
      .getInspectors()
      .then((res) => setInspectors(res.data?.inspectors || []))
      .catch(() => setInspectors([]));
  }, []);

  const downloadTemplate = (fileName: string) => {
    const link = document.createElement('a');
    link.href = `/templates/${fileName}.xlsx`;
    link.download = `${fileName}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.xlsx')) {
      setUploadErrors(['Only .xlsx files are allowed']);
      return;
    }
    setUploadLoading(true);
    setUploadErrors([]);
    setUploadMessage('Uploading…');
    setUploadSuccess(false);
    try {
      const res = await apiService.thrace.uploadData(file);
      const data = res.data;
      if (data?.has_errors || data?.success === false) {
        setUploadErrors(
          (data.errors || []).map((e: any) =>
            typeof e === 'string' ? e : e?.error || JSON.stringify(e),
          ),
        );
        setUploadMessage(data.message || 'Upload rejected');
        setUploadSuccess(false);
      } else {
        setUploadMessage(data?.message || 'Upload successful');
        setUploadSuccess(true);
      }
    } catch (e: any) {
      setUploadErrors([e?.response?.data?.detail || e?.message || 'Upload failed']);
      setUploadMessage('');
      setUploadSuccess(false);
    } finally {
      setUploadLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const patch = (idx: number, field: string, value: string) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  };

  const saveRows = async () => {
    const filled = rows.filter(
      (r) =>
        r.inspectorID &&
        (r.epiunitcountrycode?.trim() || r.farmID?.trim()) &&
        r.year &&
        r.month &&
        r.day,
    );
    if (!filled.length) {
      setSaveError(
        'Add at least one row with InspectorID, Village/Epiunit code (or Farm ID), Year, Month and Day.',
      );
      return;
    }
    setSaving(true);
    setSaveError('');
    setSaveMessage('');
    try {
      const payload = {
        rows: filled.map((r) => {
          const out: Record<string, unknown> = {
            inspectorID: Number(r.inspectorID),
            epiunitcountrycode: (r.epiunitcountrycode || '').trim(),
            farmID: (r.farmID || '').trim() || undefined,
            year: Number(r.year),
            month: Number(r.month),
            day: Number(r.day),
            enteredBy: r.enteredBy || enteredByDefault,
          };
          ACTIVITY_COLUMNS.forEach((c) => {
            if (c.kind !== 'number') return;
            if (['year', 'month', 'day'].includes(c.key)) return;
            out[c.key] = Number(r[c.key] || 0);
          });
          // Template has Tested but not Sample — mirror tested into sample for DB parity
          out.cattlesample = out.cattletested;
          out.sheepsample = out.sheeptested;
          out.goatsample = out.goattested;
          out.pigssample = out.pigtested;
          out.buffaloessample = out.buffalotested;
          out.wildsample = out.wildtested;
          return out;
        }),
      };
      const res = await apiService.thrace.saveActivities(payload);
      if (res.data?.success === false) {
        setSaveError((res.data.errors || []).join('\n') || res.data.message);
      } else {
        setSaveMessage(res.data?.message || 'Saved.');
        setRows([emptyRow(enteredByDefault)]);
      }
    } catch (e: any) {
      setSaveError(e?.response?.data?.detail || e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
          <h3 className="text-sm font-semibold text-gray-800 mb-2">Download template</h3>
          <p className="text-xs text-gray-600 mb-3">
            {templates.length === 1
              ? `Template for ${templates[0].name} activity entry.`
              : 'Country Excel templates for bulk activity entry.'}
          </p>
          <div className="flex flex-wrap gap-2">
            {templates.map((t) => (
              <button
                key={t.id}
                type="button"
                className="nav-btn text-sm"
                onClick={() => downloadTemplate(t.fileName)}
              >
                {t.name}
              </button>
            ))}
          </div>
        </div>
        <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
          <h3 className="text-sm font-semibold text-gray-800 mb-2">Upload data</h3>
          <p className="text-xs text-gray-600 mb-3">
            Upload a filled .xlsx template to import visits.
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx"
            onChange={handleFileSelect}
            disabled={uploadLoading}
            className="block w-full text-sm text-gray-500
              file:mr-4 file:py-2 file:px-4
              file:rounded-lg file:border-0
              file:text-sm file:font-semibold
              file:bg-green-50 file:text-green-700
              hover:file:bg-green-100"
          />
          {uploadLoading && <p className="text-xs text-gray-500 mt-2">Processing…</p>}
          {uploadMessage && (
            <p
              className={`text-xs mt-2 ${uploadSuccess ? 'text-green-700' : 'text-amber-700'}`}
            >
              {uploadMessage}
            </p>
          )}
          {uploadErrors.length > 0 && (
            <ul className="mt-2 text-xs text-red-600 max-h-32 overflow-y-auto space-y-1">
              {uploadErrors.map((err, i) => (
                <li key={i}>{err}</li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <div>
            <h3 className="text-sm font-semibold text-gray-800">Enter activities on screen</h3>
            <p className="text-xs text-gray-600">
              Same columns and order as the Excel template. Scroll horizontally for all fields.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              className="nav-btn text-sm"
              onClick={() => setRows((prev) => [...prev, emptyRow(enteredByDefault)])}
            >
              Add row
            </button>
            <button
              type="button"
              className="nav-btn text-sm"
              onClick={saveRows}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save visits'}
            </button>
          </div>
        </div>

        {saveError && (
          <div className="mb-2 p-3 bg-red-50 border border-red-200 text-red-700 rounded text-sm whitespace-pre-wrap">
            {saveError}
          </div>
        )}
        {saveMessage && (
          <div className="mb-2 p-3 bg-green-50 border border-green-200 text-green-800 rounded text-sm">
            {saveMessage}
          </div>
        )}

        <div className="overflow-x-auto border border-gray-200 rounded-lg max-h-[70vh]">
          <table className="min-w-max text-xs">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                {ACTIVITY_COLUMNS.map((c) => (
                  <th
                    key={c.key}
                    className="px-2 py-2 text-left font-medium text-gray-700 whitespace-nowrap border-b border-gray-200"
                  >
                    {c.label}
                  </th>
                ))}
                <th className="px-2 py-2 border-b border-gray-200" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={idx} className="border-t border-gray-100 hover:bg-gray-50">
                  {ACTIVITY_COLUMNS.map((c) => (
                    <td key={c.key} className="px-1 py-1 align-middle">
                      {c.kind === 'inspector' ? (
                        <select
                          value={r[c.key] || ''}
                          onChange={(e) => patch(idx, c.key, e.target.value)}
                          className={`${c.width || ''} px-1 py-0.5 border border-gray-200 rounded`}
                        >
                          <option value="">Select…</option>
                          {inspectors.map((ins) => (
                            <option key={ins.id} value={ins.id}>
                              {ins.name} ({ins.id})
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type={c.kind === 'number' ? 'number' : 'text'}
                          min={c.kind === 'number' ? 0 : undefined}
                          value={r[c.key] ?? ''}
                          onChange={(e) => patch(idx, c.key, e.target.value)}
                          className={`${c.width || 'w-14'} ${
                            c.kind === 'number' ? 'text-right' : ''
                          } px-1 py-0.5 border border-gray-200 rounded`}
                          placeholder={c.key === 'farmID' ? 'optional' : undefined}
                        />
                      )}
                    </td>
                  ))}
                  <td className="px-1 py-1 whitespace-nowrap">
                    <button
                      type="button"
                      className="text-red-600 hover:underline"
                      onClick={() =>
                        setRows((prev) =>
                          prev.length <= 1
                            ? [emptyRow(enteredByDefault)]
                            : prev.filter((_, i) => i !== idx),
                        )
                      }
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ThraceActivitiesPanel;
